import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { getCurrencies, getTimezones } from "go-core/utils/utils";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { OrganizationActionTypes } from "go-security/services/organizations/types";
import { UserActionTypes } from "go-security/services/users/types";
import { OrganizationInfoApi } from "../../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../../services/Api/api";
import OrganizationAddressForm from "./OrganizationAddressForm";

interface Props {
	organizationInfo: OrganizationInfoApi;
}

const OrganizationBasicInfoForm = (props: Props): JSX.Element => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { addFlash, addSuccessFlash } = useFlash();
	const organization = useSelector(selectOrganization);
	const form = useForm<OrganizationInfoApi>({
		criteriaMode: "all",
		defaultValues: props.organizationInfo,
	});
	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		formState,
		reset,
		control,
	} = form;
	const [loading, setLoading] = useState(false);

	const onSubmit = handleSubmit(async (data: OrganizationInfoApi) => {
		setLoading(true);
		try {
			const res = await api.organization().saveOrganizationBasicInfo(data);
			setLoading(false);
			dispatch({
				type: UserActionTypes.UPDATE_ORGANIZATION,
				payload: { ...res, id: organization.id },
			});
			dispatch({
				type: OrganizationActionTypes.UPDATE,
				payload: { ...res },
			});
			addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			reset(res);
		} catch (err) {
			handleError.form(err, setError, addFlash);
			setLoading(false);
		}
	});

	const timezoneOptions = getTimezones().map((tzone: string) => {
		return { label: tzone, value: tzone };
	});

	const loadLanguages = (search: string) => {
		return getLanguages()
			.map((lang) => {
				return {
					label: lang,
					value: lang,
					searchValue: `${t(`enums.languages.${lang}`, { ns: "lib" })}`,
				};
			})
			.filter((option) => option.searchValue.toLowerCase().includes(search.toLowerCase()) || !search);
	};

	const options = [
		{ label: t("enums.organization_category.CLUB"), value: "CLUB" },
		{ label: t("enums.organization_category.RESTAURANT"), value: "RESTAURANT" },
		{ label: t("enums.organization_category.CAFE"), value: "CAFE" },
		{ label: t("enums.organization_category.FOODTRUCK"), value: "FOODTRUCK" },
		{ label: t("enums.organization_category.PUB_BAR"), value: "PUB_BAR" },
		{ label: t("enums.organization_category.SUSHI"), value: "SUSHI" },
		{ label: t("enums.organization_category.SHOP"), value: "SHOP" },
		{ label: t("enums.organization_category.OTHERS"), value: "OTHERS" },
	];

	const getDefaultLanguageOptions = () =>
		["PL", "EN", "ES", "DE", "UK", "VI"].map((option) => ({
			label: t(`lib:enums.languages.${option.toLowerCase()}`),
			value: option,
		}));

	return (
		<FormDirty formState={formState} key="user-form" noValidate loading={loading} onSubmit={onSubmit}>
			<fieldset className="form-group no-gutters">
				<div className="row">
					<FormInput
						label={t("modules.setting.field.organization_name.title")}
						register={register}
						name="name"
						errors={errors}
					/>
					<FormInput
						label={t("lib:common.word.description")}
						register={register}
						name="description"
						errors={errors}
					/>
					<div className="col-md-6">
						<FormSelectGroup
							label={t("common.word.category")}
							name="category"
							defaultValue={props.organizationInfo.category}
							errors={errors}
							options={options}
							control={control}
							data-testid="category"
						/>
					</div>
					<div className="col-md-6">
						<FormSelectGroup
							label={t("modules.setting.field.currency.title")}
							name="currency"
							errors={errors}
							defaultValue={props.organizationInfo.currency}
							options={getCurrencies()}
							control={control}
							data-testid="currency"
						/>
					</div>
					<div className="col-md-6">
						<FormSelectGroup
							label={t("modules.setting.field.timezone.title")}
							name="timezone"
							errors={errors}
							defaultValue={props.organizationInfo.timezone}
							options={timezoneOptions}
							control={control}
							data-testid="timezone"
						/>
					</div>
					<div className="col-md-6">
						<FormSelectGroup
							label={t("modules.setting.field.languages.title")}
							isMulti={true}
							name="languages"
							errors={errors}
							defaultValue={props.organizationInfo.languages}
							getOptionLabel={(option) =>
								`${t(`enums.languages.${option.label ? option.label : option}`, { ns: "lib" })}`
							}
							getOptionValue={(option) => (option.value ? option.value : option)}
							loadOptions={loadLanguages}
							control={control}
							data-testid="languages"
						/>
					</div>
					<div className="col-md-6">
						<FormSelectGroup
							label={t("modules.setting.field.default_language.title")}
							name="default_language"
							errors={errors}
							options={getDefaultLanguageOptions()}
							control={control}
							defaultValue={props.organizationInfo.default_language}
							data-testid="default_language"
						/>
					</div>
				</div>
			</fieldset>
			<OrganizationAddressForm form={form} organizationInfo={props.organizationInfo} />
		</FormDirty>
	);
};

export const getLanguages = (): string[] => {
	return ["pl", "en", "de", "uk", "vi", "es"];
};

export default OrganizationBasicInfoForm;
